import { initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import { getMessaging, getToken, onMessage, type Messaging } from "firebase/messaging";
import { logger } from "./logger";

// Firebase web config derived from project google-services.json
// The appId below is the Android one -- a proper web app should be registered
// in the Firebase console. For now, push registration is graceful and will
// silently skip if the config is incomplete or a VAPID key is missing.
const firebaseConfig = {
  apiKey: "AIzaSyDj-YNEX3M3CFROs37CHTtaN5LxxJ0GOfs",
  authDomain: "xenon-container-469212-r6.firebaseapp.com",
  projectId: "xenon-container-469212-r6",
  storageBucket: "xenon-container-469212-r6.firebasestorage.app",
  messagingSenderId: "267548525082",
  // Web appId should be created in Firebase console -- using Android one as placeholder
  appId: "1:267548525082:android:31420bcc836b3a117e07a3",
};

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

function getFirebaseApp(): FirebaseApp | null {
  if (app) return app;
  try {
    app = initializeApp(firebaseConfig);
    return app;
  } catch (e) {
    logger.warn("Firebase initialization failed -- Firebase features disabled");
    return null;
  }
}

/**
 * Get Firebase Auth instance for login/register flows (039).
 */
export function getFirebaseAuth(): Auth | null {
  const firebaseApp = getFirebaseApp();
  if (!firebaseApp) return null;
  try {
    const auth = getAuth(firebaseApp);
    // E2E testing setup
    if ((window as unknown as Record<string, unknown>).__E2E_TEST__) {
      // Disable reCAPTCHA for registered test phone numbers
      auth.settings.appVerificationDisabledForTesting = true;
      // Expose signInWithCustomToken for custom token auth (no password rate limits)
      import("firebase/auth").then(({ signInWithCustomToken }) => {
        (window as any).__signInWithCustomToken__ = (token: string) => signInWithCustomToken(auth, token);
      });
      // Expose ID-token getter so E2E setup helpers can call API directly
      // with a Bearer header without going through the UI.
      (window as any).__getIdToken__ = async () => auth.currentUser?.getIdToken() ?? null;
    }
    return auth;
  } catch (e) {
    logger.warn("Firebase Auth not available");
    return null;
  }
}

function getFirebaseMessaging(): Messaging | null {
  if (messaging) return messaging;
  const firebaseApp = getFirebaseApp();
  if (!firebaseApp) return null;
  try {
    messaging = getMessaging(firebaseApp);
    return messaging;
  } catch (e) {
    logger.warn("Firebase Messaging not available -- push notifications disabled");
    return null;
  }
}

/**
 * Request notification permission and get FCM token.
 * Returns the token string, or null if unavailable/denied.
 *
 * Requires a VAPID key to be set in the Firebase console.
 * If no VAPID key is configured, this will gracefully return null.
 */
export async function getFcmToken(vapidKey?: string): Promise<string | null> {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      logger.debug("Notification permission denied");
      return null;
    }

    const msg = getFirebaseMessaging();
    if (!msg) return null;

    // Offline Support (120): the FCM background handler is now folded into the
    // unified PWA service worker (vite.config.ts -> workbox.importScripts), so
    // reuse the single active registration instead of registering a second SW
    // at the same `/` scope (which would deactivate one of them). Fall back to
    // the standalone FCM worker if the PWA SW isn't ready (e.g. dev, where the
    // PWA SW is disabled).
    let registration: ServiceWorkerRegistration | undefined;
    if ("serviceWorker" in navigator) {
      registration =
        (await navigator.serviceWorker.ready.catch(() => undefined)) ??
        (await navigator.serviceWorker
          .register("/firebase-messaging-sw.js")
          .catch(() => undefined));
    }

    const token = await getToken(msg, {
      vapidKey: vapidKey ?? undefined,
      serviceWorkerRegistration: registration,
    });

    logger.info("FCM token obtained");
    return token;
  } catch (e) {
    logger.warn("Failed to get FCM token -- push notifications disabled");
    return null;
  }
}

/**
 * Listen for foreground FCM messages.
 * Returns an unsubscribe function, or null if messaging isn't available.
 */
export function onForegroundMessage(
  callback: (payload: { title?: string; body?: string; data?: Record<string, string> }) => void,
): (() => void) | null {
  const msg = getFirebaseMessaging();
  if (!msg) return null;

  try {
    const unsubscribe = onMessage(msg, (payload) => {
      callback({
        title: payload.notification?.title,
        body: payload.notification?.body,
        data: payload.data,
      });
    });
    return unsubscribe;
  } catch (e) {
    logger.warn("Failed to set up foreground message listener");
    return null;
  }
}
